'use client';

import { useEffect, useState, useRef } from 'react';
import { useNinetailed } from '@ninetailed/experience.js-next';
import { ResolvedVisualSettings } from 'utils/pageHelpers/visualSettings';
import { getColorway } from 'utils/styles';
import { PageProps } from 'libs/contentful/queries/page/props';
import { PageVisualSettingsProps } from 'utils/pageHelpers/types';
import { Colorways } from '@/types/constants';
import { usePage } from '@/contexts/PageProvider';
import { getThemeColorFromColorway } from 'utils/themeColor';

interface ClientVisualSettingsUpdaterProps {
  page: PageProps;
  initialSettings: ResolvedVisualSettings;
}

/**
 * Client-side component that updates page visual settings when Ninetailed variants change
 * This is only used in client-side mode (draft preview)
 *
 * The component:
 * 1. Waits for the Ninetailed preview plugin to be ready
 * 2. Listens to Ninetailed profile changes via the preview plugin
 * 3. Finds the selected variant for visual settings
 * 4. Updates the page context with the new visual settings
 */
export function ClientVisualSettingsUpdater({ page, initialSettings }: ClientVisualSettingsUpdaterProps) {
  const [isReady, setIsReady] = useState(false);
  const ninetailed = useNinetailed();
  const { updateVisualSettings } = usePage();
  const lastAppliedRef = useRef<string>('');
  const originalBodyBackgroundRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Small delay to ensure preview plugin is ready (same as ClientNinetailedExperience)
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Capture original body background color only once, before any modifications
    // This must happen before early returns to ensure we capture the true original value
    // Use undefined as initial value to distinguish between "not captured yet" and "empty string"
    if (originalBodyBackgroundRef.current === undefined) {
      originalBodyBackgroundRef.current = document.body.style.backgroundColor || '';
    }

    // Only proceed if plugin is ready
    if (!isReady) {
      return;
    }

    // Only proceed if we have Ninetailed visual settings with experiments
    if (!page.pageVisualSettings?.sys?.id || !page.ninetailedData || !initialSettings.useClientSelection) {
      return;
    }

    const ninetailedId = page.pageVisualSettings.sys.id;
    const ninetailedData = page.ninetailedData.get(ninetailedId);

    if (!ninetailedData?.mappedExperiences) {
      return;
    }

    if (!ninetailed?.onSelectVariant) {
      return;
    }

    // Helper to apply settings via both context (for React components) and DOM (for page wrapper)
    const applySettings = (settings: Partial<ResolvedVisualSettings>) => {
      const settingsKey = JSON.stringify(settings);
      if (lastAppliedRef.current === settingsKey) {
        return; // Avoid unnecessary updates
      }
      lastAppliedRef.current = settingsKey;

      // Update React context - this will re-render components using usePage()
      if (updateVisualSettings) {
        updateVisualSettings(settings);
      }

      // Also update DOM directly for the page wrapper (not a React component)
      updatePageWrapperDOM(settings);
    };

    const applyVisualSettingsFromVariant = () => {
      const windowNinetailed = (window as unknown as Record<string, unknown>).ninetailed as
        | Record<string, unknown>
        | undefined;

      // Get active variant from Ninetailed's preview plugin
      let previewOverrides: Record<string, number> | null = null;

      if (windowNinetailed) {
        const plugins = windowNinetailed.plugins as Record<string, unknown> | undefined;
        const preview = plugins?.preview as Record<string, unknown> | undefined;
        previewOverrides = (preview?.experienceVariantIndexes as Record<string, number>) || null;
      }

      // If no preview overrides exist yet, check if ANY experience contains our visual settings
      // This handles the initial state before any variant is selected in the preview plugin
      let experience;

      if (previewOverrides && Object.keys(previewOverrides).length > 0) {
        // Find the experience that has a preview override
        experience = ninetailedData.mappedExperiences.find(exp => {
          return previewOverrides && previewOverrides[exp.id] !== undefined;
        });
      } else {
        // No preview overrides yet - check if we have any experience with our visual settings baseline
        // In this case, we'll use the first experience that contains our component
        experience = ninetailedData.mappedExperiences.find(exp => {
          return exp.components?.some(comp => 'id' in comp.baseline && comp.baseline.id === ninetailedId);
        });
      }

      if (!experience) {
        // No experience found at all, use baseline settings from page
        applySettings(initialSettings);
        return;
      }

      // Get the variant index from preview overrides
      // If no override exists, default to baseline (0) - Ninetailed will handle proper selection
      const variantIndex =
        previewOverrides && previewOverrides[experience.id] !== undefined ? previewOverrides[experience.id] : 0;

      // Find the component (should be the SectionPageVisualSettings)
      const component = experience.components.find(comp => 'id' in comp.baseline && comp.baseline.id === ninetailedId);

      if (!component) {
        applySettings(initialSettings);
        return;
      }

      // For variantIndex 0, use baseline; otherwise use variants array
      let variantData: unknown;
      if (variantIndex === 0) {
        // Baseline - check if we can use the already-fetched page.pageVisualSettings.pageVisualSettings
        // or if we need the component baseline
        if (page.pageVisualSettings?.pageVisualSettings) {
          // Use the already-fetched pageVisualSettings from the page query
          variantData = { pageVisualSettings: page.pageVisualSettings.pageVisualSettings };
        } else {
          // Fall back to component baseline
          variantData = component.baseline;
        }
      } else {
        // Variant - map variantIndex to variants array (1 = variants[0], 2 = variants[1], etc.)
        const actualVariantIndex = variantIndex - 1;
        if (actualVariantIndex < component.variants.length) {
          const selectedVariant = component.variants[actualVariantIndex];
          // Check if the selected variant is hidden/empty (has no pageVisualSettings)
          // This can happen when Contentful experiment has empty slots
          if (
            selectedVariant &&
            typeof selectedVariant === 'object' &&
            'hidden' in selectedVariant &&
            selectedVariant.hidden === true
          ) {
            // Find the first non-hidden variant that has pageVisualSettings
            const nonEmptyVariant = component.variants.find(
              v => v && typeof v === 'object' && !('hidden' in v && v.hidden) && 'pageVisualSettings' in v,
            );
            variantData = nonEmptyVariant || selectedVariant;
          } else {
            variantData = selectedVariant;
          }
        } else {
          // Index out of bounds, use baseline
          variantData = component.baseline;
        }
      }

      // Extract visual settings from the variant data
      if (variantData && typeof variantData === 'object' && 'pageVisualSettings' in variantData) {
        const variantSettings = (variantData as { pageVisualSettings?: PageVisualSettingsProps }).pageVisualSettings;

        if (variantSettings) {
          // Handle both object format {name: "Primary"} and string format "Primary" for colorways
          const extractColorway = (color: unknown): Colorways | undefined => {
            if (!color) return undefined;
            if (typeof color === 'string') return color as Colorways;
            if (typeof color === 'object' && 'name' in color) {
              return (color as { name: string }).name as Colorways;
            }
            return undefined;
          };

          const extractedPageColor = extractColorway(variantSettings.pageColor);
          const extractedFooterColor = extractColorway(variantSettings.footerColor);

          const settingsToApply = {
            pageColor: extractedPageColor ?? page.pageColor,
            footerColor: extractedFooterColor ?? page.footerColor,
            showFooterKeyline: variantSettings.showFooterKeyline ?? page.showFooterKeyline,
            transparentNav: variantSettings.transparentNav ?? page.transparentNav,
            moduleThresholdNavColorChange:
              variantSettings.moduleThresholdNavColorChange ?? page.moduleThresholdNavColorChange,
          };

          applySettings(settingsToApply);
        }
      } else {
        // No visual settings in variant, use baseline
        applySettings(initialSettings);
      }
    };

    // Initial application
    applyVisualSettingsFromVariant();

    // Subscribe to variant changes using Ninetailed's onSelectVariant API
    const unsubscribe = ninetailed.onSelectVariant(
      { baseline: { id: ninetailedId }, experiences: ninetailedData.mappedExperiences } as unknown as Parameters<
        typeof ninetailed.onSelectVariant
      >[0],
      () => {
        // When variant changes, detect and apply the new settings
        applyVisualSettingsFromVariant();
      },
    );

    // Cleanup subscription on unmount
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
      // Reset body background color to original value to remove inline style for next page
      document.body.style.backgroundColor = originalBodyBackgroundRef.current ?? '';
      // Reset lastAppliedRef so settings are re-applied when effect re-runs
      lastAppliedRef.current = '';
    };
  }, [page, initialSettings, ninetailed, isReady, updateVisualSettings]);

  // This component doesn't render anything
  return null;
}

/**
 * Swaps the colorway classes on an element
 * @param element - The element to swap the colorway classes on
 * @param colorway - The colorway to swap the classes for
 */
function swapColorwayClasses(element: HTMLElement, colorway: Colorways | undefined) {
  const existingClasses = Array.from(element.classList).filter(cls => cls.endsWith('-colorway'));
  existingClasses.forEach(cls => element.classList.remove(cls));

  if (colorway) {
    const newClass = getColorway(colorway);
    element.classList.add(newClass);
  }
}

/**
 * Updates DOM elements that don't consume context via usePage()
 * This includes:
 * - Page wrapper: Server component, uses local variable
 * - Animated background: Conditionally rendered, needs visibility toggle
 * - Page content: Inner div that changes class based on animated background
 * - Header nav: Server component, uses local variable for transparentNav
 * - Footer: Server component, uses local variable for footerColor
 * - Footer keyline: Receives showFooterKeyline as prop
 * - Disclosure banner: Fixed position element that uses pageColor
 * - Theme-color meta tag: Controls iOS Safari notch color

 */
function updatePageWrapperDOM(settings: Partial<ResolvedVisualSettings>) {
  const pageWrapper = document.querySelector('[data-page-wrapper]');
  const animatedBackground = document.querySelector('[data-animated-background]');
  const pageContent = document.querySelector('[data-page-content]');
  const headerNav = document.querySelector('[data-header-nav]');
  const footer = document.querySelector('footer[data-footer]');
  const disclosureBanner = document.querySelector('[data-disclosure-banner]');

  // Check if the new pageColor requires animated background
  let showAnimatedBackground = false;
  if ('pageColor' in settings) {
    const pageColor = settings.pageColor;
    const pageColorName =
      pageColor && typeof pageColor === 'object' && 'name' in pageColor
        ? (pageColor as { name: string }).name
        : pageColor;
    showAnimatedBackground = pageColorName === Colorways.DarkGreenAnimation;

    // Update theme-color meta tag for iOS Safari notch
    const themeColor = getThemeColorFromColorway(pageColor);
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', themeColor);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'theme-color';
      meta.content = themeColor;
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Update body background color
    document.body.style.backgroundColor = themeColor;
  }

  if (pageWrapper && 'pageColor' in settings) {
    swapColorwayClasses(pageWrapper as HTMLElement, settings.pageColor);

    // Toggle animated background visibility
    if (animatedBackground) {
      if (showAnimatedBackground) {
        animatedBackground.classList.remove('hidden');
      } else {
        animatedBackground.classList.add('hidden');
      }
    }

    // Update page content div class based on animated background state
    if (pageContent) {
      if (showAnimatedBackground) {
        // When animated background is shown, content needs relative z-10 positioning
        pageContent.className = 'relative z-10';
      } else {
        // When no animated background, content uses the colorway class
        swapColorwayClasses(pageContent as HTMLElement, settings.pageColor);
        pageContent.classList.remove('relative', 'z-10');
      }
    }
  }

  // Update disclosure banner with pageColor (it's fixed position and uses same color as page)
  if (disclosureBanner && 'pageColor' in settings) {
    swapColorwayClasses(disclosureBanner as HTMLElement, settings.pageColor);
  }

  // Update header nav classes
  if (headerNav) {
    const header = headerNav as HTMLElement;

    // Update transparentNav classes
    if ('transparentNav' in settings) {
      if (settings.transparentNav) {
        // transparentNav: true - use bg-transparent
        header.classList.remove('bg-inherit', 'bg-[image:inherit]');
        header.classList.add('bg-transparent');
      } else {
        // transparentNav: false - use bg-inherit bg-[image:inherit]
        header.classList.remove('bg-transparent');
        header.classList.add('bg-inherit', 'bg-[image:inherit]');
      }
    }

    // Update animated background classes on header nav
    // When animated background is shown, header needs the colorway class for proper background
    // When not shown, header uses bg-inherit
    if ('pageColor' in settings) {
      // First remove any existing colorway classes from header (for animated background styling)
      const existingColorwayClasses = Array.from(header.classList).filter(cls => cls.endsWith('-colorway'));
      existingColorwayClasses.forEach(cls => header.classList.remove(cls));

      if (showAnimatedBackground) {
        // Add the colorway class when animated background is shown
        const colorwayClass = getColorway(settings.pageColor ?? Colorways.Primary);
        header.classList.add(colorwayClass);
        // Also remove bg-inherit since we're using the colorway class for background
        header.classList.remove('bg-inherit');
      } else {
        // Add bg-inherit when no animated background (unless transparentNav is true)
        if (!settings.transparentNav) {
          header.classList.add('bg-inherit');
        }
      }
    }
  }

  if (footer) {
    if ('footerColor' in settings) {
      swapColorwayClasses(footer as HTMLElement, settings.footerColor);
    }

    // Handle footer keyline visibility
    if (settings.showFooterKeyline !== undefined) {
      const keyline = footer.querySelector('.keyline');
      const footerSection = footer.querySelector('.footer-section');

      if (keyline) {
        if (settings.showFooterKeyline) {
          keyline.classList.remove('hidden');
          keyline.classList.add('block');
          footerSection?.classList.add('pt-6', 'md:pt-12');
        } else {
          keyline.classList.remove('block');
          keyline.classList.add('hidden');
          footerSection?.classList.remove('pt-6', 'md:pt-12');
        }
      }
    }
  }
}
